import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase, type Insert, type Row } from '@/lib/supabase'
import type { JobKind } from '@/lib/jobs'

export type Contact = Row<'contacts'>

interface ContactJob {
  id: string
  kind: JobKind
  title: string | null
  event_date: string
  archived_at: string | null
}

export interface ContactWithJobs extends Contact {
  jobs: ContactJob[]
}

const JOB_FIELDS = 'id, kind, title, event_date, archived_at'

/**
 * Everyone in the system (28 Oct 2026): couples and clients, with the
 * projects they are on. A person can be the main contact on one project and
 * the partner on another, so both links are read and merged.
 */
export function useContacts(showArchived: boolean) {
  return useQuery({
    queryKey: ['contacts', showArchived],
    queryFn: async (): Promise<ContactWithJobs[]> => {
      let query = supabase
        .from('contacts')
        .select(
          `*, as_primary:jobs!jobs_primary_contact_id_fkey ( ${JOB_FIELDS} ), as_partner:jobs!jobs_partner_contact_id_fkey ( ${JOB_FIELDS} )`,
        )
        .order('first_name')
      if (!showArchived) query = query.is('archived_at', null)
      const { data, error } = await query
      if (error) throw error
      return data.map(({ as_primary, as_partner, ...contact }) => ({
        ...contact,
        jobs: [...as_primary, ...as_partner].sort((a, b) => b.event_date.localeCompare(a.event_date)),
      }))
    },
  })
}

export function useSaveContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: Insert<'contacts'> }) => {
      const { error } = id
        ? await supabase.from('contacts').update(values).eq('id', id)
        : await supabase.from('contacts').insert(values)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
      void queryClient.invalidateQueries({ queryKey: ['job'] })
      void queryClient.invalidateQueries({ queryKey: ['pipeline-board'] })
    },
  })
}
